import { EmptyState, theme } from '@ketch-com/deck'
import { Box, Typography } from '@mui/material'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts'

export interface PieChartSubCategory {
  name: string
  percentage: number
  count: number
}

export interface PieChartDataItem {
  name: string
  value: number
  color: string
  subCategories?: PieChartSubCategory[]
  originalCount: number
}

interface DataPieChartProps {
  data: PieChartDataItem[]
  title?: string
  noDataMessage?: string
  noDataSubMessage?: string
  height?: number
  width?: string | number
  innerRadius?: number
  outerRadius?: number
  paddingAngle?: number
  responsiveContainerWidth?: number
  responsiveContainerHeight?: number
}

const CategoryTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (!active || !payload || !payload.length) return null

  const data = payload[0].payload as PieChartDataItem

  return (
    <Box
      sx={{
        background: theme.palette.darkDusk.main,
        borderRadius: theme.spacing(0.75),
        p: theme.spacing(1.25),
        minWidth: theme.spacing(31.25),
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {data.name === 'Others' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
          {data.subCategories?.map((sub: PieChartSubCategory) => (
            <Typography key={sub.name} variant="smallLabel" color={theme.palette.Common.Container}>
              {Number.isInteger(sub.percentage) ? `${sub.percentage}.0` : sub.percentage}% &nbsp;
              <Typography variant="smallBody">{sub.name}</Typography>
            </Typography>
          ))}
        </Box>
      ) : (
        <>
          <Typography variant="smallLabel" color={theme.palette.Common.Container} sx={{ mb: theme.spacing(0.75) }}>
            {data.name}
          </Typography>
          <Typography variant="smallBody" color={theme.palette.Common.Container}>
            {Number.isInteger(data.value) ? `${data.value}.0` : data.value}%
          </Typography>
        </>
      )}
    </Box>
  )
}

export const DataPieChart = ({
  data,
  height = 130,
  innerRadius = 30,
  outerRadius = 70,
  paddingAngle = 0,
  responsiveContainerWidth = 140,
  responsiveContainerHeight = 140,
}: DataPieChartProps) => {
  if (!data?.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height,
          textAlign: 'center',
          marginTop: '48px',
        }}
      >
        <EmptyState
          size="small"
          colorTheme={theme.palette.Black.o48}
          iconName="FPie"
          title="No Data"
          subTitle="Review your settings and connections, or wait for the scan to finish processing."
        />
      </Box>
    )
  }

  return (
    <Box display="flex" alignItems={'center'} gap={2} width="100%">
      <ResponsiveContainer width={responsiveContainerWidth} height={responsiveContainerHeight}>
        <PieChart>
          <Pie
            data={data}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={paddingAngle}
            fill="#8884d8"
            cx="50%"
            cy="50%"
            dataKey="value"
            isAnimationActive={false}
            label={false}
          >
            {data.map(entry => (
              <Cell key={`cell-${entry.name}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={CategoryTooltip} />
        </PieChart>
      </ResponsiveContainer>

      <Box display={'flex'} flexDirection="column" gap={1} flex={1}>
        {data.map(entry => (
          <Box
            key={entry.name}
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: theme.spacing(0.75),
                height: theme.spacing(0.75),
                borderRadius: '50%',
                backgroundColor: entry.color,
                flexShrink: 0,
              }}
            />
            <Typography variant="smallLabel" minWidth={50} textAlign="right" mr={1}>
              {Number.isInteger(entry.value) ? `${entry.value}.0` : entry.value}%
            </Typography>
            <Typography variant="smallBody">{entry.name.charAt(0).toUpperCase() + entry.name.slice(1)}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
